/**
 * SOLID Review
 * S — Strong. One use case: announce a claim — open after a peek, blind before one, as the plan says.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandAnnounce, ICommandClaimAccepted, IPayloadAnnounce } from '../../domain/ports/schemas/TableMoves';
import type { IPayloadServerClaim } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/**
 * The roller announces (FE1's announce): an open claim after a peek, a blind pass before one — the plan says which, and a
 * refused blind pass does not advance the phase.
 */
export class AnnounceUseCase implements IUseCase<ICommandAnnounce, TPortOutcome>, TServerRoundTrip<IPropsClientTableInput, ICommandAnnounce, IPayloadAnnounce, TAnswered<ICommandClaimAccepted['answer']>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandAnnounce): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandAnnounce) {
    return this.deps.decisions.planAnnounce(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadAnnounce) {
    const claim: IPayloadServerClaim = { roundId: plan.roundId, playerId: plan.playerId, score: plan.score };
    return plan.blind ? this.deps.rounds.announceBlind(ctx, claim) : this.deps.rounds.announceClaim(ctx, claim);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadAnnounce, answered: TAnswered<ICommandClaimAccepted['answer']>) {
    const accepted: ICommandClaimAccepted = { request: plan, answer: answered.value };
    return this.deps.decisions.claimAccepted(ctx, input, accepted);
  }
}
